import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { AuthUser } from "../types";
import {
  getAccessToken,
  setAccessToken,
  verifyMagicLinkWeb,
  refreshAccessTokenWeb,
  logoutWeb,
  scheduleProactiveRefresh,
  clearRefreshTimer,
  type LoginResponse,
} from "../lib/auth";
import { apolloClient } from "../lib/apollo-client";

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Call after magic link verification */
  loginWithMagicLink: (token: string) => Promise<void>;
  /** Call on `/auth/oidc/landing` after backend set refresh cookie */
  completeOidcWebSession: () => Promise<void>;
  /** Call to end session */
  logout: () => Promise<void>;
  /** Current access token (for debug/inspection) */
  accessToken: string | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function mapLoginResponseToUser(res: LoginResponse): AuthUser {
  return {
    id: res.user.id,
    email: res.user.email,
    firstName: res.user.firstName,
    lastName: res.user.lastName,
    role: res.user.role as AuthUser["role"],
    tier: res.user.tier as AuthUser["tier"],
    profilePicture: res.user.profilePicture,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Guard: when a magic link login is in progress or completed,
  // the silent refresh init() must not overwrite the session.
  const magicLinkActiveRef = useRef(false);
  /** OIDC web landing page is exchanging the httpOnly cookie for a session */
  const oidcLandingActiveRef = useRef(false);

  // Refresh handler (shared by proactive timer and manual calls)
  const doRefresh = useCallback(async (): Promise<boolean> => {
    const result = await refreshAccessTokenWeb();
    if (result) {
      setAccessToken(result.token);
      setUser(mapLoginResponseToUser(result));
      scheduleProactiveRefresh(result.token, async () => {
        await doRefresh();
      });
      return true;
    }
    setAccessToken(null);
    setUser(null);
    clearRefreshTimer();
    return false;
  }, []);

  // On mount: attempt silent refresh using httpOnly cookie
  useEffect(() => {
    let cancelled = false;

    async function init() {
      // Skip silent refresh on auth handshake pages — dedicated flows own refresh.
      const path = window.location.pathname;
      if (
        path === "/verify" ||
        path === "/auth/oidc/landing" ||
        path === "/auth/oidc/error"
      ) {
        setIsLoading(false);
        return;
      }

      const result = await refreshAccessTokenWeb();
      if (cancelled) return;

      // If a magic link login started/completed while we were refreshing,
      // do NOT overwrite the session — the magic link result takes priority.
      if (magicLinkActiveRef.current || oidcLandingActiveRef.current) {
        setIsLoading(false);
        return;
      }

      if (result) {
        setAccessToken(result.token);
        setUser(mapLoginResponseToUser(result));
        scheduleProactiveRefresh(result.token, async () => {
          await doRefresh();
        });
      }
      setIsLoading(false);
    }

    void init();

    return () => {
      cancelled = true;
      clearRefreshTimer();
    };
  }, [doRefresh]);

  const loginWithMagicLink = useCallback(
    async (magicLinkToken: string) => {
      // Signal to init() that a magic link login is taking priority
      magicLinkActiveRef.current = true;

      const result = await verifyMagicLinkWeb(magicLinkToken);
      setAccessToken(result.token);
      setUser(mapLoginResponseToUser(result));
      scheduleProactiveRefresh(result.token, async () => {
        await doRefresh();
      });

      // Reset Apollo store — clears cached data from previous session AND
      // refetches all active queries so the new session gets fresh data.
      // Wrapped in try-catch: resetStore() can throw if active query refetches
      // fail, but the login itself already succeeded — don't break the flow.
      try {
        await apolloClient.resetStore();
      } catch {
        // Non-critical — cache will be refreshed on next navigation
      }
    },
    [doRefresh]
  );

  const completeOidcWebSession = useCallback(async () => {
    oidcLandingActiveRef.current = true;
    try {
      const result = await refreshAccessTokenWeb();
      if (!result) {
        throw new Error("OIDC_WEB_SESSION_EMPTY");
      }
      setAccessToken(result.token);
      setUser(mapLoginResponseToUser(result));
      scheduleProactiveRefresh(result.token, async () => {
        await doRefresh();
      });
      try {
        await apolloClient.resetStore();
      } catch {
        // Non-critical — cache will refresh on navigation
      }
    } finally {
      oidcLandingActiveRef.current = false;
    }
  }, [doRefresh]);

  const logout = useCallback(async () => {
    await logoutWeb();
    setUser(null);
    magicLinkActiveRef.current = false;
    oidcLandingActiveRef.current = false;
    // Clear cached data from the ending session
    await apolloClient.resetStore();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      loginWithMagicLink,
      completeOidcWebSession,
      logout,
      accessToken: getAccessToken(),
    }),
    [user, isLoading, loginWithMagicLink, completeOidcWebSession, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
